/**
 * OnboardingScreen — Wizard d'onboarding multi-étapes
 *
 * Affiché UNE fois au 1er login, BLOQUANT (pas de skip).
 * Collecte tous les essentiels pour personnaliser l'expérience :
 *
 *  Étape 1 : Identité       (nom, prénom, email si manquant, genre)
 *  Étape 2 : Date de naissance (picker natif iOS/Android)
 *  Étape 3 : Morphologie    (taille cm, poids kg, silhouette)
 *  Étape 4 : Tailles        (hauts, bas, chaussures, veste — pré-remplies)
 *
 * Une fois soumis, les infos sont insérées en DB. Au prochain démarrage,
 * App.js détecte que les essentiels sont présents → onboarding plus jamais
 * affiché. L'user peut tout modifier via Profil > Modifier mon profil.
 */
import React, { useState, useMemo, useEffect } from 'react';
import {
  View, Text, TextInput, TouchableOpacity, ScrollView,
  StyleSheet, SafeAreaView, StatusBar, KeyboardAvoidingView,
  Platform, ActivityIndicator, Alert, Modal,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n';
import { colors } from '../theme';
import {
  estimateSizes, silhouetteOptions, sizeOptions,
} from '../utils/morphoHelper';

// v1.12.117 — sur web, fallback HTML5 <input type="date"> (le picker RN
// ne marche pas correctement sur react-native-web).
let DateTimePicker = null;
if (Platform.OS !== 'web') {
  try { DateTimePicker = require('@react-native-community/datetimepicker').default; } catch {}
}
import WebDateInput from '../components/WebDateInput';

const GOLD      = '#D4AF72';
const GOLD_DARK = '#B8860B';

const MONTHS_FR = ['janvier','février','mars','avril','mai','juin',
  'juillet','août','septembre','octobre','novembre','décembre'];
// v1.12.125 — âge minimum 6 ans
// v1.12.157 — âge minimum 15 ans (RGPD France, LIL art.45). Avant : 6 ans.
function getMaxDate() { const d = new Date(); d.setFullYear(d.getFullYear() - 15); return d; }
function getMinDate() { const d = new Date(); d.setFullYear(d.getFullYear() - 110); return d; }
function getDefaultDate() { const d = new Date(); d.setFullYear(d.getFullYear() - 30); return d; }
function formatDateISO(date) {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
function formatDateFR(iso) {
  if (!iso) return '';
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  return `${parseInt(m[3], 10)} ${MONTHS_FR[parseInt(m[2], 10) - 1]} ${m[1]}`;
}

export default function OnboardingScreen({ onComplete }) {
  const { user, updateUserProfile } = useAuth();
  const { t, lang } = useI18n();

  const isAppleRelay = !!(user?.email && user.email.toLowerCase().endsWith('@privaterelay.appleid.com'));
  // v1.12.933 — Apple Guideline 4 : Sign in with Apple fournit déjà nom + email
  // (relais inclus) via Authentication Services. On ne DOIT jamais forcer un user
  // Apple à ressaisir/vérifier un email réel ni à fournir un nom. Pour ces users :
  // email relais accepté tel quel + nom optionnel. Seul le genre (fonctionnel,
  // requis par le moteur de matching) reste demandé à l'étape 1.
  const isAppleUser  = !!(user?.provider === 'apple' || isAppleRelay);
  const hasFirstName = !!(user?.firstName && user.firstName.trim().length >= 2);
  const hasLastName  = !!(user?.lastName  && user.lastName.trim().length  >= 2);
  const hasGender    = !!(user?.gender && ['M','F','N'].includes(user.gender));
  const hasEmail     = !!(user?.email && !isAppleRelay);

  // ═══ Démarrer à la première étape incomplète ═══
  // Si l'user a déjà rempli partiellement son profil (ex: nom/genre/DDN OK
  // mais pas la morpho), on saute directement à l'étape qui manque pour
  // ne pas l'obliger à cliquer "Suivant" sur des écrans déjà remplis.
  const initialStep = useMemo(() => {
    // Étape 1 : identité (nom, prénom, email, genre)
    const s1 = hasFirstName && hasLastName && hasEmail && hasGender;
    if (!s1) return 1;
    // Étape 2 : DDN
    const s2 = !!user?.birthDate;
    if (!s2) return 2;
    // Étape 3 : morpho
    const s3 = !!(user?.height_cm && user?.weight_kg && user?.silhouette);
    if (!s3) return 3;
    // Sinon étape 4 (tailles)
    return 4;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // calculé une seule fois au mount

  const insets = useSafeAreaInsets(); // v1.13 — remonter le footer au-dessus de la barre Android
  const [step, setStep] = useState(initialStep);
  const totalSteps = 4;

  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName,  setLastName]  = useState(user?.lastName  || '');
  const [email,     setEmail]     = useState(hasEmail ? user.email : '');
  const [gender,    setGender]    = useState(user?.gender || null);

  // v1.12.124 — Vérification email pour Apple users avec relay
  // Phase: 'idle' (pas envoyé) | 'sending' | 'sent' (code envoyé)
  //        | 'verifying' | 'verified' (OK) | 'error'
  const [emailVerifyPhase, setEmailVerifyPhase] = useState(hasEmail ? 'verified' : 'idle');
  const [emailCode, setEmailCode] = useState('');
  const [emailVerifyError, setEmailVerifyError] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Tick le cooldown chaque seconde
  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const sendEmailCode = async () => {
    const em = (email || '').trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) {
      setEmailVerifyError(t('invalid_email_format'));
      return;
    }
    setEmailVerifyError(null);
    setEmailVerifyPhase('sending');
    try {
      const { AWS_CONFIG } = require('../config/aws_config');
      let token;
      try {
        const { getAuthToken } = await import('../services/authHelpers');
        token = await getAuthToken();
      } catch {}
      if (!token) {
        try {
          const SecureStore = await import('expo-secure-store');
          token = await SecureStore.getItemAsync('outfit_access');
        } catch {}
      }
      const res = await fetch(`${AWS_CONFIG.apiUrl}/profile/email-verify-send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ email: em }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (data?.error === 'rate_limited') {
          setResendCooldown(data.retry_after_sec || 60);
          setEmailVerifyPhase('sent');
          setEmailVerifyError(t('too_many_requests_retry_in').replace('{seconds}', String(data.retry_after_sec)));
        } else if (data?.error === 'relay_email_not_allowed') {
          setEmailVerifyPhase('idle');
          setEmailVerifyError(t('apple_relay_blocked'));
        } else {
          setEmailVerifyPhase('idle');
          setEmailVerifyError(data?.error || t('error_status').replace('{status}', String(res.status)));
        }
        return;
      }
      setEmailVerifyPhase('sent');
      setResendCooldown(60);
    } catch (e) {
      setEmailVerifyPhase('idle');
      setEmailVerifyError(e?.message || t('network_error_short'));
    }
  };

  const confirmEmailCode = async () => {
    if (!/^\d{6}$/.test(emailCode)) {
      setEmailVerifyError(t('code_must_be_6_digits'));
      return;
    }
    setEmailVerifyError(null);
    setEmailVerifyPhase('verifying');
    try {
      const { AWS_CONFIG } = require('../config/aws_config');
      let token;
      try {
        const { getAuthToken } = await import('../services/authHelpers');
        token = await getAuthToken();
      } catch {}
      if (!token) {
        try {
          const SecureStore = await import('expo-secure-store');
          token = await SecureStore.getItemAsync('outfit_access');
        } catch {}
      }
      const em = (email || '').trim().toLowerCase();
      const res = await fetch(`${AWS_CONFIG.apiUrl}/profile/email-verify-confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ email: em, code: emailCode }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setEmailVerifyPhase('sent');
        if (data?.error === 'invalid_code') {
          setEmailVerifyError(t('incorrect_code_attempts_left').replace('{count}', String(data.attempts_left ?? 0)));
        } else if (data?.error === 'code_expired') {
          setEmailVerifyError(t('code_expired_request_new'));
          setEmailCode('');
          setEmailVerifyPhase('idle');
        } else if (data?.error === 'too_many_attempts') {
          setEmailVerifyError(t('too_many_attempts_request_new'));
          setEmailCode('');
          setEmailVerifyPhase('idle');
        } else if (data?.error === 'email_mismatch') {
          setEmailVerifyError(t('email_changed_resend_code'));
        } else {
          setEmailVerifyError(data?.error || t('error_status').replace('{status}', String(res.status)));
        }
        return;
      }
      setEmailVerifyPhase('verified');
      setEmailVerifyError(null);
    } catch (e) {
      setEmailVerifyPhase('sent');
      setEmailVerifyError(e?.message || t('network_error_short'));
    }
  };

  // Si l'user modifie l'email après vérification → reset
  const onEmailChangeWithReset = (v) => {
    setEmail(v);
    if (emailVerifyPhase === 'sent' || emailVerifyPhase === 'verified') {
      setEmailVerifyPhase('idle');
      setEmailCode('');
      setEmailVerifyError(null);
    }
  };

  const [birthDate, setBirthDate] = useState(user?.birthDate || '');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [tempDate, setTempDate] = useState(null);

  const [heightCm,   setHeightCm]   = useState(user?.height_cm ? String(user.height_cm) : '');
  const [weightKg,   setWeightKg]   = useState(user?.weight_kg ? String(user.weight_kg) : '');
  const [silhouette, setSilhouette] = useState(user?.silhouette || null);

  const initSizes = user?.clothing_sizes || {};
  const [sizeTops,    setSizeTops]    = useState(initSizes.tops    || '');
  const [sizeBottoms, setSizeBottoms] = useState(initSizes.bottoms || '');
  const [sizeShoes,   setSizeShoes]   = useState(initSizes.shoes   || '');
  const [sizeJacket,  setSizeJacket]  = useState(initSizes.jacket  || '');

  const [submitting, setSubmitting] = useState(false);

  // Auto-pré-remplissage des tailles à partir taille/poids
  useEffect(() => {
    const h = parseInt(heightCm, 10);
    const w = parseInt(weightKg, 10);
    if (!h || !w || !gender) return;
    if (sizeTops && sizeBottoms && sizeShoes && sizeJacket) return;
    const est = estimateSizes(h, w, gender);
    if (!sizeTops    && est.tops)    setSizeTops(est.tops);
    if (!sizeBottoms && est.bottoms) setSizeBottoms(est.bottoms);
    if (!sizeShoes   && est.shoes)   setSizeShoes(est.shoes);
    if (!sizeJacket  && est.jacket)  setSizeJacket(est.jacket);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heightCm, weightKg, gender]);

  const step1Valid = useMemo(() => {
    const gnOk = ['M','F','N'].includes(gender);
    // v1.12.933 — Users Apple : nom + email NON obligatoires (Guideline 4).
    // Seul le genre est requis (fonctionnel pour le moteur de matching).
    if (isAppleUser) return gnOk;
    const fnOk = firstName.trim().length >= 2;
    const lnOk = lastName.trim().length >= 2;
    // v1.12.124 — si pas de vrai email, on EXIGE la vérification par code
    const emOk = hasEmail || (
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || '').trim()) &&
      emailVerifyPhase === 'verified'
    );
    return fnOk && lnOk && emOk && gnOk;
  }, [firstName, lastName, email, gender, hasEmail, emailVerifyPhase, isAppleUser]);

  const step2Valid = useMemo(() => {
    if (!birthDate) return false;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(birthDate)) return false;
    const d = new Date(birthDate);
    if (isNaN(d.getTime())) return false;
    // v1.12.157 — âge minimum 15 ans (RGPD France, LIL art.45). Avant : 6 ans.
    // Plafond 110 ans (garde-fou contre les fautes de saisie).
    const ageY = (new Date() - d) / (365.25 * 24 * 3600 * 1000);
    return ageY >= 15 && ageY <= 110;
  }, [birthDate]);

  const step3Valid = useMemo(() => {
    const h = parseInt(heightCm, 10); const w = parseInt(weightKg, 10);
    if (!h || h < 140 || h > 220) return false;
    if (!w || w < 35  || w > 200) return false;
    return true;
  }, [heightCm, weightKg]);

  const step4Valid = !!(sizeTops && sizeBottoms && sizeShoes && sizeJacket);

  const canNext =
    step === 1 ? step1Valid :
    step === 2 ? step2Valid :
    step === 3 ? step3Valid :
    step === 4 ? step4Valid : false;

  const handleSubmit = async () => {
    if (!step4Valid || submitting) return;
    setSubmitting(true);
    const fn = firstName.trim();
    const ln = lastName.trim();
    const payload = {
      gender, birth_date: birthDate,
      height_cm: parseInt(heightCm, 10),
      weight_kg: parseInt(weightKg, 10),
      silhouette,
      clothing_sizes: { tops: sizeTops, bottoms: sizeBottoms, shoes: sizeShoes, jacket: sizeJacket },
    };
    // v1.12.933 — N'envoyer nom/prénom que s'ils sont renseignés. Pour les users
    // Apple sans nom (champ optionnel), on n'écrase PAS le profil avec du vide.
    if (fn) payload.first_name = fn;
    if (ln) payload.last_name  = ln;
    if (fn || ln) payload.name = `${fn} ${ln}`.trim();
    // Email saisi manuellement : seulement pour les NON-Apple sans email
    // (les users Apple conservent leur email/relais fourni par Apple à l'inscription).
    if (!hasEmail && !isAppleUser && email && email.includes('@')) {
      payload.email = email.trim().toLowerCase();
    }

    try {
      const { AWS_CONFIG } = require('../config/aws_config');
      let token;
      try {
        const { getAuthToken } = await import('../services/authHelpers');
        token = await getAuthToken();
      } catch {}
      if (!token) {
        try {
          const SecureStore = await import('expo-secure-store');
          token = await SecureStore.getItemAsync('outfit_access');
        } catch {}
      }
      const res = await fetch(`${AWS_CONFIG.apiUrl}/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });
      const bodyText = await res.text();
      if (!res.ok) {
        let errMsg = `HTTP ${res.status}`;
        try { errMsg = JSON.parse(bodyText).error || errMsg; } catch {}
        throw new Error(errMsg);
      }
      updateUserProfile({
        ...(payload.first_name ? { firstName: payload.first_name } : {}),
        ...(payload.last_name  ? { lastName:  payload.last_name }  : {}),
        ...(payload.name       ? { name:      payload.name }       : {}),
        gender:    payload.gender,
        birthDate: payload.birth_date,
        height_cm: payload.height_cm,
        weight_kg: payload.weight_kg,
        silhouette: payload.silhouette,
        clothing_sizes: payload.clothing_sizes,
        ...(payload.email ? { email: payload.email } : {}),
        profileHydrated: true,
      });
      onComplete?.(payload);
    } catch (e) {
      console.warn('[Onboarding] submit error:', e?.message || e);
      Alert.alert(t('error'), `${t('cannot_save_profile')}\n\n${e?.message || t('unknown_error')}`);
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Skip : enregistre uniquement les infos disponibles (Google/Apple) ───
  // Permet à l'user de différer le remplissage. profileHydrated = true pour
  // que l'onboarding ne réapparaisse pas. L'user complétera via Profil.
  const handleSkip = async () => {
    if (submitting) return;

    // Confirmation avant skip
    Alert.alert(
      t('skip_onboarding_title'),
      t('skip_onboarding_msg'),
      [
        { text: t('continue_form'), style: 'cancel' },
        {
          text: t('skip_for_now'),
          style: 'destructive',
          onPress: async () => {
            setSubmitting(true);

            // Construire payload PARTIEL avec uniquement les infos qu'on a.
            // Tout le reste reste null/vide en DB.
            const partial = {};
            const fn = (firstName || user?.firstName || '').trim();
            const ln = (lastName  || user?.lastName  || '').trim();
            if (fn) partial.first_name = fn;
            if (ln) partial.last_name  = ln;
            if (fn || ln) partial.name = `${fn} ${ln}`.trim();
            if (gender) partial.gender = gender;
            if (birthDate) partial.birth_date = birthDate;
            if (heightCm) partial.height_cm = parseInt(heightCm, 10);
            if (weightKg) partial.weight_kg = parseInt(weightKg, 10);
            if (silhouette) partial.silhouette = silhouette;
            const sizes = {};
            if (sizeTops) sizes.tops = sizeTops;
            if (sizeBottoms) sizes.bottoms = sizeBottoms;
            if (sizeShoes) sizes.shoes = sizeShoes;
            if (sizeJacket) sizes.jacket = sizeJacket;
            if (Object.keys(sizes).length > 0) partial.clothing_sizes = sizes;
            if (!hasEmail && email && email.includes('@')) {
              partial.email = email.trim().toLowerCase();
            }

            try {
              const { AWS_CONFIG } = require('../config/aws_config');
              let token;
              try {
                const { getAuthToken } = await import('../services/authHelpers');
                token = await getAuthToken();
              } catch {}
              if (!token) {
                try {
                  const SecureStore = await import('expo-secure-store');
                  token = await SecureStore.getItemAsync('outfit_access');
                } catch {}
              }

              // PUT vers l'API uniquement si on a au moins un champ à envoyer
              if (Object.keys(partial).length > 0) {
                await fetch(`${AWS_CONFIG.apiUrl}/profile`, {
                  method: 'PUT',
                  headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                  },
                  body: JSON.stringify(partial),
                }).catch(e => console.warn('[Onboarding] skip PUT non bloquant:', e?.message));
              }
            } catch (e) {
              console.warn('[Onboarding] skip error (non bloquant):', e?.message);
            }

            // Mettre \u00e0 jour le contexte local (m\u00eame si PUT API a \u00e9chou\u00e9 :
            // l'onboarding ne dois pas r\u00e9apparaitre dans tous les cas)
            updateUserProfile({
              ...(partial.first_name ? { firstName: partial.first_name } : {}),
              ...(partial.last_name  ? { lastName:  partial.last_name }  : {}),
              ...(partial.name       ? { name:      partial.name }       : {}),
              ...(partial.gender     ? { gender:    partial.gender }     : {}),
              ...(partial.birth_date ? { birthDate: partial.birth_date } : {}),
              ...(partial.height_cm  ? { height_cm: partial.height_cm }  : {}),
              ...(partial.weight_kg  ? { weight_kg: partial.weight_kg }  : {}),
              ...(partial.silhouette ? { silhouette: partial.silhouette }: {}),
              ...(partial.clothing_sizes ? { clothing_sizes: partial.clothing_sizes } : {}),
              ...(partial.email      ? { email: partial.email }          : {}),
              profileHydrated: true,
            });

            setSubmitting(false);
            onComplete?.(partial);
          },
        },
      ],
    );
  };

  // ─── Render header ───
  const renderHeader = () => (
    <View style={s.header}>
      {/* ═══ Bouton "Passer" en haut à droite (discret) ═══ */}
      {/* Permet de différer le remplissage. Les infos déjà connues (Google/Apple) */}
      {/* sont sauvegardées. L'user peut compléter via Profil > Modifier mon profil. */}
      <TouchableOpacity
        onPress={handleSkip}
        disabled={submitting}
        style={s.skipBtn}
        activeOpacity={0.7}>
        <Text style={s.skipBtnText}>{t('skip')}</Text>
      </TouchableOpacity>
      <View style={s.progressBar}>
        {[1, 2, 3, 4].map(n => (
          <View key={n} style={[
            s.progressDot,
            n === step && s.progressDotActive,
            n < step && s.progressDotDone,
          ]} />
        ))}
      </View>
      <Text style={s.stepCounter}>{t('step_x_of_y').replace('{current}', String(step)).replace('{total}', String(totalSteps))}</Text>
    </View>
  );

  // ─── Étape 1 ───
  const renderStep1 = () => {
    const allStep1Filled = hasFirstName && hasLastName && hasEmail && hasGender;
    return (
      <ScrollView contentContainerStyle={s.content} keyboardShouldPersistTaps="handled">
        <Text style={s.title}>{t('onboarding_welcome')}</Text>
        <Text style={s.subtitle}>{t('onboarding_intro_desc')}</Text>

        {allStep1Filled && (
          <View style={s.recapBox}>
            <Text style={s.recapTitle}>{t('onboarding_recap_title')}</Text>
            <Text style={s.recapLine}>{user.firstName} {user.lastName}</Text>
            <Text style={s.recapLine}>{user.email}</Text>
            <Text style={s.recapLine}>{user.gender === 'M' ? t('gender_man') : user.gender === 'F' ? t('gender_woman') : t('gender_other')}</Text>
            <Text style={s.recapHint}>{t('onboarding_recap_hint')}</Text>
          </View>
        )}

        {!hasFirstName && (
          <View style={s.field}>
            <Text style={s.label}>{t('first_name')}{isAppleUser ? ` (${t('optional')})` : ''}</Text>
            <TextInput style={s.input} value={firstName} onChangeText={setFirstName}
              placeholder={t('first_name_placeholder')} placeholderTextColor={colors.text.faint}
              autoCapitalize="words" autoComplete="given-name" textContentType="givenName" />
          </View>
        )}

        {!hasLastName && (
          <View style={s.field}>
            <Text style={s.label}>{t('last_name')}{isAppleUser ? ` (${t('optional')})` : ''}</Text>
            <TextInput style={s.input} value={lastName} onChangeText={setLastName}
              placeholder={t('last_name_placeholder')} placeholderTextColor={colors.text.faint}
              autoCapitalize="words" autoComplete="family-name" textContentType="familyName" />
          </View>
        )}

        {/* v1.12.933 — Apple Guideline 4 : on ne demande PAS d'email aux users
            Sign in with Apple. L'email (relais inclus) est déjà fourni par Apple. */}
        {!hasEmail && !isAppleUser && (
          <View style={s.field}>
            <Text style={s.label}>{t('email_label')}</Text>
            {isAppleRelay && (
              <Text style={s.hint}>{t('apple_relay_email_hint')}</Text>
            )}
            <TextInput
              style={[s.input, emailVerifyPhase === 'verified' && { backgroundColor: '#F0FDF4', borderColor: '#22C55E' }]}
              value={email}
              onChangeText={onEmailChangeWithReset}
              placeholder={t('email_placeholder')}
              placeholderTextColor={colors.text.faint}
              keyboardType="email-address"
              autoCapitalize="none"
              autoCorrect={false}
              autoComplete="email"
              textContentType="emailAddress"
              editable={emailVerifyPhase !== 'verified' && emailVerifyPhase !== 'sending' && emailVerifyPhase !== 'verifying'}
            />

            {/* États de vérification */}
            {emailVerifyPhase === 'verified' && (
              <Text style={{ fontSize: 13, color: '#22C55E', marginTop: 6, fontWeight: '600' }}>
                {t('email_verified')}
              </Text>
            )}

            {emailVerifyPhase === 'idle' && (
              <TouchableOpacity
                onPress={sendEmailCode}
                disabled={!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || '').trim())}
                style={{
                  marginTop: 10,
                  paddingVertical: 12,
                  paddingHorizontal: 16,
                  borderRadius: 10,
                  backgroundColor: '#9B7B5A',
                  alignItems: 'center',
                  opacity: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || '').trim()) ? 1 : 0.4,
                }}
              >
                <Text style={{ color: '#FFFFFF', fontSize: 13, fontWeight: '600', letterSpacing: 0.5 }}>
                  {t('send_verification_code')}
                </Text>
              </TouchableOpacity>
            )}

            {emailVerifyPhase === 'sending' && (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 10, justifyContent: 'center' }}>
                <ActivityIndicator size="small" color="#9B7B5A" />
                <Text style={{ fontSize: 13, color: '#7A6F60' }}>{t('sending_code')}</Text>
              </View>
            )}

            {(emailVerifyPhase === 'sent' || emailVerifyPhase === 'verifying') && (
              <View style={{ marginTop: 12, gap: 8 }}>
                <Text style={{ fontSize: 12, color: '#7A6F60' }}>
                  {t('six_digit_code_received')}
                </Text>
                <TextInput
                  style={[s.input, { letterSpacing: 8, textAlign: 'center', fontSize: 18 }]}
                  value={emailCode}
                  onChangeText={(v) => { setEmailCode(v.replace(/\D/g, '').slice(0, 6)); setEmailVerifyError(null); }}
                  placeholder="000000"
                  placeholderTextColor={colors.text.faint}
                  keyboardType="number-pad"
                  maxLength={6}
                  autoComplete="one-time-code"
                  textContentType="oneTimeCode"
                  editable={emailVerifyPhase !== 'verifying'}
                />
                <View style={{ flexDirection: 'row', gap: 8 }}>
                  <TouchableOpacity
                    onPress={confirmEmailCode}
                    disabled={emailCode.length !== 6 || emailVerifyPhase === 'verifying'}
                    style={{
                      flex: 2,
                      paddingVertical: 12,
                      borderRadius: 10,
                      backgroundColor: '#9B7B5A',
                      alignItems: 'center',
                      opacity: emailCode.length === 6 && emailVerifyPhase !== 'verifying' ? 1 : 0.4,
                    }}
                  >
                    {emailVerifyPhase === 'verifying'
                      ? <ActivityIndicator size="small" color="#FFFFFF" />
                      : <Text style={{ color: '#FFFFFF', fontSize: 13, fontWeight: '600', letterSpacing: 0.5 }}>{t('verify')}</Text>}
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={sendEmailCode}
                    disabled={resendCooldown > 0 || emailVerifyPhase === 'verifying'}
                    style={{
                      flex: 1,
                      paddingVertical: 12,
                      borderRadius: 10,
                      borderWidth: 1,
                      borderColor: '#E8E0D4',
                      backgroundColor: '#F5EFE8',
                      alignItems: 'center',
                      opacity: resendCooldown > 0 || emailVerifyPhase === 'verifying' ? 0.5 : 1,
                    }}
                  >
                    <Text style={{ color: '#5F584F', fontSize: 12, fontWeight: '500' }}>
                      {resendCooldown > 0 ? `${resendCooldown}s` : t('resend')}
                    </Text>
                  </TouchableOpacity>
                </View>
              </View>
            )}

            {emailVerifyError && emailVerifyPhase !== 'verified' && (
              <Text style={{ fontSize: 12, color: '#C62828', marginTop: 6, fontWeight: '500' }}>
                {emailVerifyError}
              </Text>
            )}
          </View>
        )}

        {!hasGender && (
          <View style={s.field}>
            <Text style={s.label}>{t('gender')}</Text>
            <View style={s.row}>
              {[
                { id: 'F', label: t('gender_woman') },
                { id: 'M', label: t('gender_man') },
              ].map(opt => (
                <TouchableOpacity key={opt.id} onPress={() => setGender(opt.id)}
                  style={[s.chip, gender === opt.id && s.chipActive]} activeOpacity={0.85}>
                  <Text style={[s.chipText, gender === opt.id && s.chipTextActive]}>{opt.label}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        )}
      </ScrollView>
    );
  };

  // ─── Étape 2 ───
  const renderStep2 = () => (
    <ScrollView contentContainerStyle={s.content} keyboardShouldPersistTaps="handled">
      <Text style={s.title}>{t('your_date_of_birth')}</Text>
      <Text style={s.subtitle}>{t('age_adapts_styles')}</Text>

      <View style={s.field}>
        {Platform.OS === 'web' ? (
          <WebDateInput
            value={birthDate}
            onChange={setBirthDate}
            min={formatDateISO(getMinDate())}
            max={formatDateISO(getMaxDate())}
          />
        ) : DateTimePicker ? (
          <TouchableOpacity style={s.dateBtn} onPress={() => {
            // v1.13.61 — Android : API IMPÉRATIVE (recommandée). Le composant
            // <DateTimePicker> déclaratif se réinitialisait à la date initiale
            // (re-render → value recalculée) → impossible de saisir. L'ouverture
            // impérative gère le dialog nativement, onChange fire une fois.
            if (Platform.OS === 'android') {
              try {
                const { DateTimePickerAndroid } = require('@react-native-community/datetimepicker');
                DateTimePickerAndroid.open({
                  value: birthDate ? new Date(birthDate) : getDefaultDate(),
                  mode: 'date',
                  minimumDate: getMinDate(),
                  maximumDate: getMaxDate(),
                  onChange: (event, d) => {
                    if (event?.type !== 'dismissed' && d) setBirthDate(formatDateISO(d));
                  },
                });
              } catch (e) { console.warn('[Onboarding] DateTimePickerAndroid:', e?.message); }
              return;
            }
            setTempDate(birthDate ? new Date(birthDate) : getDefaultDate());
            setShowDatePicker(true);
          }} activeOpacity={0.85}>
            <Text style={[s.dateBtnText, !birthDate && { color: colors.text.faint }]}>
              {birthDate ? formatDateFR(birthDate) : t('select_birth_date')}
            </Text>
            <Text style={s.dateBtnIcon}>📅</Text>
          </TouchableOpacity>
        ) : (
          <TextInput style={s.input} value={birthDate} onChangeText={setBirthDate}
            placeholder={t('date_format_placeholder')} placeholderTextColor={colors.text.faint}
            keyboardType="numbers-and-punctuation" maxLength={10} />
        )}
        {/* v1.12.157 — âge minimum 15 ans (RGPD France, LIL art.45) */}
        {birthDate && !step2Valid && (
          <Text style={{ fontSize: 12, color: '#C62828', marginTop: 6, fontWeight: '500' }}>
            {t('minimum_age_required')}
          </Text>
        )}
        <Text style={{ fontSize: 11, color: colors.text.faint, marginTop: 6, fontStyle: 'italic' }}>
          {t('minimum_age_gdpr_hint')}
        </Text>
      </View>

      {Platform.OS === 'ios' && DateTimePicker && (
        <Modal visible={showDatePicker} transparent animationType="slide"
          onRequestClose={() => setShowDatePicker(false)} statusBarTranslucent>
          <TouchableOpacity activeOpacity={1} style={s.iosBg}
            onPress={() => setShowDatePicker(false)}>
            <TouchableOpacity activeOpacity={1} onPress={() => {}} style={s.iosSheet}>
              <View style={s.iosHandle} />
              <View style={s.iosHeader}>
                <TouchableOpacity onPress={() => setShowDatePicker(false)}>
                  <Text style={s.iosCancel}>{t('cancel')}</Text>
                </TouchableOpacity>
                <Text style={s.iosTitle}>{t('date_of_birth')}</Text>
                <TouchableOpacity onPress={() => {
                  if (tempDate) setBirthDate(formatDateISO(tempDate));
                  setShowDatePicker(false);
                }}>
                  <Text style={s.iosDone}>{t('validate')}</Text>
                </TouchableOpacity>
              </View>
              <View style={s.iosPickerBox}>
                <DateTimePicker
                  value={tempDate || (birthDate ? new Date(birthDate) : getDefaultDate())}
                  mode="date" display="spinner"
                  minimumDate={getMinDate()} maximumDate={getMaxDate()}
                  locale={lang === 'en' ? 'en-US' : 'fr-FR'}
                  onChange={(_, d) => { if (d) setTempDate(d); }}
                  style={{ height: 220, backgroundColor: '#FFF' }} />
              </View>
            </TouchableOpacity>
          </TouchableOpacity>
        </Modal>
      )}

      {/* v1.13.61 — Android : plus de picker déclaratif (remplacé par l'API
          impérative DateTimePickerAndroid.open dans le onPress ci-dessus). */}
    </ScrollView>
  );

  // ─── Étape 3 ───
  const renderStep3 = () => (
    <ScrollView contentContainerStyle={s.content} keyboardShouldPersistTaps="handled">
      <Text style={s.title}>{t('your_morphology')}</Text>
      <Text style={s.subtitle}>{t('morphology_explanation')}</Text>

      <View style={s.row}>
        <View style={[s.field, { flex: 1, marginRight: 8 }]}>
          <Text style={s.label}>{t('height_cm')}</Text>
          <TextInput style={s.input} value={heightCm}
            onChangeText={txt => setHeightCm(txt.replace(/\D/g, '').slice(0, 3))}
            placeholder="175" placeholderTextColor={colors.text.faint}
            keyboardType="number-pad" maxLength={3} />
        </View>
        <View style={[s.field, { flex: 1, marginLeft: 8 }]}>
          <Text style={s.label}>{t('weight_kg')}</Text>
          <TextInput style={s.input} value={weightKg}
            onChangeText={txt => setWeightKg(txt.replace(/\D/g, '').slice(0, 3))}
            placeholder="70" placeholderTextColor={colors.text.faint}
            keyboardType="number-pad" maxLength={3} />
        </View>
      </View>

    </ScrollView>
  );

  // ─── Étape 4 ───
  const renderStep4 = () => (
    <ScrollView contentContainerStyle={s.content} keyboardShouldPersistTaps="handled">
      <Text style={s.title}>{t('your_sizes')}</Text>
      <Text style={s.subtitle}>{t('sizes_prefilled_from_morphology')}</Text>

      <SizeRow label={t('tops_label_with_examples')} type="tops" gender={gender}
        value={sizeTops} onChange={setSizeTops} />
      <SizeRow
        label={t('bottoms_label_with_examples_eu')}
        type="bottoms" gender={gender}
        value={sizeBottoms} onChange={setSizeBottoms} />
      <SizeRow label={t('jacket_coat_label')} type="jacket" gender={gender}
        value={sizeJacket} onChange={setSizeJacket} />
      <SizeRow label={t('shoes_eu_label')} type="shoes" gender={gender}
        value={sizeShoes} onChange={setSizeShoes} />
    </ScrollView>
  );

  // ─── Footer ───
  const renderFooter = () => (
    <View style={[s.footer, { paddingBottom: Math.max(insets.bottom, 16) }]}>
      {step > 1 && (
        <TouchableOpacity onPress={() => setStep(step - 1)}
          style={s.backBtn} activeOpacity={0.85}>
          <Text style={s.backBtnText}>{t('back_arrow')}</Text>
        </TouchableOpacity>
      )}
      <TouchableOpacity
        onPress={() => { if (step < totalSteps) setStep(step + 1); else handleSubmit(); }}
        disabled={!canNext || submitting}
        style={[s.nextBtn, (!canNext || submitting) && s.nextBtnDisabled]}
        activeOpacity={0.88}>
        {submitting ? <ActivityIndicator color="#FFF" /> : (
          <Text style={s.nextBtnText}>{step < totalSteps ? t('next_arrow') : t('finish')}</Text>
        )}
      </TouchableOpacity>
    </View>
  );

  return (
    <SafeAreaView style={s.safe}>
      <StatusBar barStyle="dark-content" />
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        style={{ flex: 1 }}>
        {renderHeader()}
        <View style={{ flex: 1 }}>
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}
        </View>
        {renderFooter()}
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

// ─── Composant SizeRow réutilisable ───
function SizeRow({ label, type, gender, value, onChange }) {
  const opts = sizeOptions(type, gender || 'F');
  return (
    <View style={s.field}>
      <Text style={s.label}>{label}</Text>
      <View style={[s.row, { flexWrap: 'wrap', gap: 6 }]}>
        {opts.map(v => (
          <TouchableOpacity key={v} onPress={() => onChange(v)}
            style={[s.sizeChip, value === v && s.sizeChipActive]} activeOpacity={0.85}>
            <Text style={[s.sizeChipText, value === v && s.sizeChipTextActive]}>{v}</Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );
}

const s = StyleSheet.create({
  safe: { flex: 1, backgroundColor: '#FAFAFA' },
  header: {
    paddingHorizontal: 24,
    paddingTop: Platform.OS === 'android' ? (StatusBar.currentHeight || 0) + 12 : 12,
    paddingBottom: 16,
    borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: '#E5E5E5',
  },
  skipBtn: {
    position: 'absolute',
    top: Platform.OS === 'android' ? (StatusBar.currentHeight || 0) + 12 : 12,
    right: 18,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 8,
    zIndex: 10,
  },
  skipBtnText: {
    fontSize: 13,
    color: colors.text.secondary,
    fontWeight: '500',
    letterSpacing: 0.3,
  },
  progressBar: { flexDirection: 'row', gap: 8, marginBottom: 8 },
  progressDot: { flex: 1, height: 4, borderRadius: 2, backgroundColor: '#E5E5E5' },
  progressDotActive: { backgroundColor: GOLD },
  progressDotDone:   { backgroundColor: GOLD_DARK },
  stepCounter: { fontSize: 11, color: colors.text.faint, fontWeight: '500', letterSpacing: 0.5 },
  content: { paddingHorizontal: 24, paddingTop: 28, paddingBottom: 40 },
  title: { fontSize: 28, fontWeight: '600', color: colors.text.primary, marginBottom: 8, letterSpacing: -0.3 },
  subtitle: { fontSize: 14, color: colors.text.secondary, lineHeight: 20, marginBottom: 28 },
  field: { marginBottom: 22 },
  label: { fontSize: 13, fontWeight: '600', color: colors.text.primary, marginBottom: 8, letterSpacing: 0.2 },
  hint: { fontSize: 12, color: colors.text.secondary, marginBottom: 8, lineHeight: 17, fontStyle: 'italic' },
  // ─── Récap des infos déjà remplies ───
  recapBox: {
    backgroundColor: '#FFF7E0', borderRadius: 12, padding: 16, marginBottom: 24,
    borderWidth: 1, borderColor: GOLD,
  },
  recapTitle: {
    fontSize: 13, fontWeight: '700', color: GOLD_DARK,
    letterSpacing: 0.4, marginBottom: 8,
  },
  recapLine: {
    fontSize: 14, color: colors.text.primary, marginBottom: 2,
  },
  recapHint: {
    fontSize: 12, color: GOLD_DARK, fontStyle: 'italic',
    marginTop: 8, lineHeight: 17,
  },
  input: {
    backgroundColor: '#FFFFFF', borderRadius: 10, paddingHorizontal: 16, paddingVertical: 14,
    fontSize: 16, color: colors.text.primary, borderWidth: 1, borderColor: '#E5E5E5',
  },
  row: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  chip: {
    paddingHorizontal: 16, paddingVertical: 10,
    borderRadius: 20, borderWidth: 1.5, borderColor: '#E5E5E5', backgroundColor: '#FFFFFF',
  },
  chipActive: { borderColor: GOLD, backgroundColor: '#FFF7E0' },
  chipText: { fontSize: 14, color: colors.text.primary, fontWeight: '500' },
  chipTextActive: { color: GOLD_DARK, fontWeight: '700' },
  sizeChip: {
    minWidth: 50, paddingHorizontal: 10, paddingVertical: 8,
    borderRadius: 8, borderWidth: 1.5, borderColor: '#E5E5E5',
    backgroundColor: '#FFFFFF', alignItems: 'center',
  },
  sizeChipActive: { borderColor: GOLD, backgroundColor: '#FFF7E0' },
  sizeChipText: { fontSize: 13, color: colors.text.primary, fontWeight: '600' },
  sizeChipTextActive: { color: GOLD_DARK, fontWeight: '700' },

  // Silhouette card (avec hint descriptif sous le label)
  silCard: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    paddingHorizontal: 16, paddingVertical: 14,
    borderRadius: 12, borderWidth: 1.5, borderColor: '#E5E5E5',
    backgroundColor: '#FFFFFF',
  },
  silCardActive: { borderColor: GOLD, backgroundColor: '#FFF7E0' },
  silCardLabel: { fontSize: 15, fontWeight: '600', color: colors.text.primary, marginBottom: 2 },
  silCardLabelActive: { color: GOLD_DARK, fontWeight: '700' },
  silCardHint: { fontSize: 11, color: colors.text.secondary, lineHeight: 15 },
  silCardHintActive: { color: GOLD_DARK, opacity: 0.85 },
  silCardDot: {
    width: 18, height: 18, borderRadius: 9,
    borderWidth: 2, borderColor: '#CCC', backgroundColor: 'transparent',
  },
  silCardDotActive: {
    borderColor: GOLD_DARK, backgroundColor: GOLD_DARK,
  },
  dateBtn: {
    backgroundColor: '#FFFFFF', borderRadius: 10,
    paddingHorizontal: 16, paddingVertical: 16,
    borderWidth: 1, borderColor: '#E5E5E5',
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
  },
  dateBtnText: { fontSize: 16, color: colors.text.primary, fontWeight: '500' },
  dateBtnIcon: { fontSize: 18 },
  iosBg: { flex: 1, backgroundColor: 'transparent', justifyContent: 'flex-end' },
  iosSheet: {
    backgroundColor: '#FFFFFF', borderTopLeftRadius: 20, borderTopRightRadius: 20,
    paddingBottom: 28,
    shadowColor: '#000', shadowOffset: { width: 0, height: -4 },
    shadowOpacity: 0.25, shadowRadius: 12, elevation: 16,
  },
  iosHandle: {
    alignSelf: 'center', width: 40, height: 4, borderRadius: 2,
    backgroundColor: '#E5E5E5', marginTop: 8, marginBottom: 4,
  },
  iosHeader: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 16, paddingVertical: 14,
    borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: '#E5E5E5',
  },
  iosTitle: { fontSize: 15, fontWeight: '700', color: colors.text.primary },
  iosCancel: { fontSize: 15, color: colors.text.secondary },
  iosDone: { fontSize: 15, fontWeight: '700', color: GOLD_DARK },
  iosPickerBox: { backgroundColor: '#FFFFFF', paddingTop: 8 },
  footer: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    paddingHorizontal: 24, paddingVertical: 16,
    borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: '#E5E5E5',
    backgroundColor: '#FFFFFF',
  },
  backBtn: {
    paddingHorizontal: 18, paddingVertical: 14,
    borderRadius: 10, borderWidth: 1, borderColor: '#E5E5E5', backgroundColor: '#FFFFFF',
  },
  backBtnText: { fontSize: 14, color: colors.text.secondary, fontWeight: '600' },
  nextBtn: {
    flex: 1, paddingVertical: 14, borderRadius: 10,
    backgroundColor: GOLD_DARK, alignItems: 'center', justifyContent: 'center',
  },
  nextBtnDisabled: { backgroundColor: '#CCC' },
  nextBtnText: { color: '#FFFFFF', fontSize: 15, fontWeight: '700', letterSpacing: 0.3 },
});
